import { useState } from "react";
import { Box, Dialog, IconButton, Skeleton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import { BrandMatchResult } from "../constants";
import { formatAcquiredDate } from "../Leads/LeadsTable";
import { colors, typography, spacing } from "../../../../assets/styles/tokens";

interface BrandMatchModalMobileProps {
  data: LeadsManagementData;
}

const BRAND_CARD_SKELETON_COUNT = 4;

function matchResultColor(result: BrandMatchResult): string {
  if (result === "Match") return colors.Success[600].$value;
  if (result === "No Match") return colors.Error[600].$value;
  return colors.GrayLightMode[500].$value;
}

// Mobile-only variant of the shared BrandMatchModal (Leads/BrandMatchModal.tsx)
// — kept as a separate component rather than making that one responsive,
// since the layouts diverge structurally: the desktop version renders one
// wide condition table with a column per brand, while this one stacks each
// brand as its own card with its own collapsible condition list.
export default function BrandMatchModalMobile({ data }: BrandMatchModalMobileProps) {
  const {
    matchModalOpen,
    matchLead,
    matchLoading,
    matchError,
    matchDiagnostic,
    closeBrandMatch,
    onMatchModalExited,
    rerunBrandMatch,
    canReportToBrand,
    reportToBrand,
    reportingBrandKey,
  } = data;

  const [expandedBrandKey, setExpandedBrandKey] = useState<string | null>(null);

  const eligibleOtherBrands = matchDiagnostic
    ? matchDiagnostic.brands.filter((brand) => brand.eligible && !brand.assigned)
    : [];
  const hasEligibleOthers = eligibleOtherBrands.length > 0;
  const totalConditions = matchDiagnostic?.rows.length ?? 0;

  return (
    <Dialog
      open={matchModalOpen}
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        closeBrandMatch();
      }}
      TransitionProps={{ onExited: onMatchModalExited }}
      fullScreen
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: spacing.Spacing.xl.$value,
          py: spacing.Spacing.lg.$value,
          borderBottom: `1px solid ${colors.GrayLightMode[200].$value}`,
        }}
      >
        <IconButton size="small" onClick={closeBrandMatch}>
          <CloseIcon fontSize="small" sx={{ color: colors.GrayLightMode[700].$value }} />
        </IconButton>
        <Typography
          sx={{
            fontFamily: typography.Font_Family.$value,
            fontWeight: typography.Font_weight.Semibold.$value,
            fontSize: typography.Size.Text.md.$value,
            color: colors.GrayLightMode[900].$value,
          }}
        >
          Check Matches
        </Typography>
        <Box sx={{ width: 32 }} />
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: spacing.Spacing.xl.$value, py: spacing.Spacing.xl.$value }}>
        {matchLead && (
          <Typography
            sx={{
              fontFamily: typography.Font_Family.$value,
              fontWeight: typography.Font_weight.Semibold.$value,
              fontSize: typography.Size.Text.sm.$value,
              lineHeight: typography.Line_height.text.sm.$value,
              color: colors.GrayLightMode[900].$value,
              textTransform: "capitalize",
              mb: spacing.Spacing.md.$value,
            }}
          >
            {matchLead.name}{" "}
            <Typography
              component="span"
              sx={{
                fontFamily: typography.Font_Family.$value,
                fontWeight: typography.Font_weight.Semibold.$value,
                fontSize: typography.Size.Text.xs.$value,
                lineHeight: typography.Line_height.text.xs.$value,
                color: colors.GrayLightMode[500].$value,
              }}
            >
              ({matchLead.id})
            </Typography>
          </Typography>
        )}

        {matchLoading && (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.125,
                borderRadius: spacing.Radius.md.$value,
                px: spacing.Spacing.xl.$value,
                py: spacing.Spacing.md.$value,
                bgcolor: colors.Brand[25].$value,
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  border: `2.5px solid ${colors.Brand[200].$value}`,
                  borderTopColor: colors.Brand[600].$value,
                  "@keyframes brandMatchSpinMobile": { to: { transform: "rotate(360deg)" } },
                  animation: "brandMatchSpinMobile 0.8s linear infinite",
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: 13, color: colors.Brand[700].$value }}>
                Running brand match query for lead #{matchLead?.id}…
              </Typography>
            </Box>
            {Array.from({ length: BRAND_CARD_SKELETON_COUNT }).map((_, index) => (
              <Skeleton key={index} variant="rounded" sx={{ height: 96, mt: spacing.Spacing.lg.$value, bgcolor: colors.GrayLightMode[100].$value }} />
            ))}
          </Box>
        )}

        {!matchLoading && matchError && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: spacing.Spacing.md1.$value,
              bgcolor: colors.Error[50].$value,
              border: `1px solid ${colors.Error[200].$value}`,
              borderRadius: spacing.Radius.md.$value,
              px: spacing.Spacing.xl.$value,
              py: spacing.Spacing.lg1.$value,
            }}
          >
            <InfoOutlinedIcon sx={{ color: colors.Error[600].$value, fontSize: 20 }} />
            <Typography sx={{ fontSize: 13, color: colors.Error[600].$value, flex: 1 }}>{matchError}</Typography>
            <Typography
              onClick={rerunBrandMatch}
              sx={{ fontSize: 12, fontWeight: 500, color: colors.Brand[700].$value, cursor: "pointer", whiteSpace: "nowrap" }}
            >
              Re-run
            </Typography>
          </Box>
        )}

        {!matchLoading && !matchError && matchDiagnostic && (
          <Box>
            {matchDiagnostic.rows.map((row, index) => (
              <Box
                key={row.key}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: spacing.Spacing.xl.$value,
                  py: spacing.Spacing.sm.$value,
                  borderTop: index === 0 ? "none" : `1px solid ${colors.GrayLightMode[200].$value}`,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: typography.Font_Family.$value,
                    fontWeight: typography.Font_weight.Regular.$value,
                    fontSize: typography.Size.Text.xs.$value,
                    lineHeight: typography.Line_height.text.xs.$value,
                    letterSpacing: "0%",
                    color: colors.GrayLightMode[700].$value,
                  }}
                >
                  {row.nice.toUpperCase()}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: typography.Font_Family.$value,
                    fontWeight: typography.Font_weight.Semibold.$value,
                    fontSize: typography.Size.Text.sm.$value,
                    lineHeight: typography.Line_height.text.sm.$value,
                    letterSpacing: "0%",
                    color: colors.GrayLightMode[900].$value,
                    textAlign: "right",
                    textTransform: "capitalize",
                  }}
                >
                  {row.leadAnswer}
                </Typography>
              </Box>
            ))}

            <Box sx={{ mx: -2, mt: spacing.Spacing.xl.$value, borderTop: `1px solid ${colors.GrayLightMode[200].$value}` }} />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: spacing.Spacing.md.$value,
                borderRadius: spacing.Radius.md.$value,
                px: spacing.Spacing.xl.$value,
                py: spacing.Spacing.md1.$value,
                mt: spacing.Spacing.md.$value,
                bgcolor: hasEligibleOthers ? colors.Success[50].$value : colors.GrayLightMode[50].$value,
                border: `1px solid ${hasEligibleOthers ? colors.Success[600].$value : colors.GrayLightMode[200].$value}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  color: hasEligibleOthers ? colors.Success[600].$value : colors.GrayLightMode[700].$value,
                  textAlign: "center",
                  flex: 1,
                }}
              >
                {hasEligibleOthers
                  ? `💡 This lead meets every condition of ${eligibleOtherBrands.length} additional brand${eligibleOtherBrands.length > 1 ? "s" : ""} beyond ${matchLead?.brand ?? "its assigned brand"}.`
                  : `No additional brand accepts this lead on all ${totalConditions} condition${totalConditions === 1 ? "" : "s"}.`}
              </Typography>
            </Box>

            {[...matchDiagnostic.brands]
              .sort((a, b) => Number(b.assigned) - Number(a.assigned))
              .map((brand) => {
              const barPercent = brand.totalConditions ? (brand.matchedCount / brand.totalConditions) * 100 : 0;
              const canReport = brand.eligible && !brand.assigned && canReportToBrand;
              const isReportingThisBrand = reportingBrandKey === brand.key;
              const reportDisabled = reportingBrandKey !== null && !isReportingThisBrand;
              const isExpanded = expandedBrandKey === brand.key;

              return (
                <Box
                  key={brand.key}
                  sx={{
                    mt: spacing.Spacing.lg.$value,
                    borderRadius: spacing.Radius.md.$value,
                    p: spacing.Spacing.md.$value,
                    border: `1px solid ${brand.assigned ? colors.Brand[600].$value : colors.GrayLightMode[200].$value}`,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: spacing.Spacing.sm.$value }}>
                    {brand.assigned && (
                      <CheckCircleIcon sx={{ fontSize: 18, color: colors.Brand[600].$value }} />
                    )}
                    <Typography
                      sx={{
                        fontFamily: typography.Font_Family.$value,
                        fontWeight: typography.Font_weight.Semibold.$value,
                        fontSize: typography.Size.Text.sm.$value,
                        color: colors.GrayLightMode[900].$value,
                      }}
                    >
                      {brand.name}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: spacing.Spacing.sm.$value }}>
                    <Typography sx={{ fontSize: 14, color: colors.GrayLightMode[900].$value }}>
                      <Box component="span" sx={{ fontWeight: 700 }}>
                        {brand.matchedCount}
                      </Box>{" "}
                      / {brand.totalConditions} conditions met
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        px: spacing.Spacing.md.$value,
                        py: spacing.Spacing.xxs.$value,
                        borderRadius: spacing.Radius.xs.$value,
                        fontSize: 12,
                        fontWeight: 600,
                        color: brand.eligible ? colors.Success[600].$value : colors.Error[600].$value,
                        bgcolor: brand.eligible ? colors.Success[50].$value : colors.Error[50].$value,
                      }}
                    >
                      {brand.eligible ? "Eligible" : "Not Eligible"}
                    </Box>
                  </Box>

                  <Box sx={{ height: 6, borderRadius: spacing.Radius.xl.$value, bgcolor: colors.GrayLightMode[100].$value, mt: spacing.Spacing.md.$value, overflow: "hidden" }}>
                    <Box
                      sx={{
                        height: "100%",
                        borderRadius: spacing.Radius.xl.$value,
                        width: `${barPercent}%`,
                        bgcolor: brand.eligible ? colors.Success[600].$value : colors.Error[600].$value,
                      }}
                    />
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: spacing.Spacing.md.$value }}>
                    {canReport ? (
                      <Typography
                        onClick={() => !reportDisabled && !isReportingThisBrand && reportToBrand(brand.key)}
                        sx={{
                          fontFamily: typography.Font_Family.$value,
                          fontWeight: typography.Font_weight.Regular.$value,
                          fontSize: typography.Size.Text.xs.$value,
                          lineHeight: typography.Line_height.text.xs.$value,
                          letterSpacing: "0%",
                          verticalAlign: "middle",
                          color: reportDisabled ? colors.GrayLightMode[300].$value : colors.GrayLightMode[700].$value,
                          cursor: reportDisabled || isReportingThisBrand ? "default" : "pointer",
                          textDecoration: isReportingThisBrand ? "none" : "underline",
                          textDecorationStyle: "solid",
                        }}
                      >
                        {isReportingThisBrand ? "Reporting…" : "Report to this brand"}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontSize: 12, color: colors.GrayLightMode[500].$value }}>
                        {brand.assigned
                          ? `Assigned on ${formatAcquiredDate(matchLead?.date ?? "")}`
                          : !brand.eligible
                          ? "Conditions not met"
                          : ""}
                      </Typography>
                    )}
                    <Box
                      onClick={() => setExpandedBrandKey(isExpanded ? null : brand.key)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: spacing.Spacing.xxs.$value,
                        cursor: "pointer",
                        color: colors.GrayLightMode[500].$value,
                      }}
                    >
                      <Typography sx={{ fontSize: 12 }}>Conditions</Typography>
                      <ExpandMoreIcon
                        sx={{
                          fontSize: 16,
                          transform: isExpanded ? "rotate(180deg)" : "none",
                          transition: "transform 0.15s",
                        }}
                      />
                    </Box>
                  </Box>

                  {isExpanded && (
                    <Box sx={{ mt: spacing.Spacing.md1.$value, borderTop: `1px solid ${colors.GrayLightMode[200].$value}` }}>
                      {matchDiagnostic.rows.map((row) => {
                        const cell = row.cells.find((c) => c.brandKey === brand.key);
                        if (!cell) return null;
                        return (
                          <Box
                            key={row.key}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              py: spacing.Spacing.xs.$value,
                              borderTop: `1px solid ${colors.GrayLightMode[200].$value}`,
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: typography.Font_Family.$value,
                                fontWeight: typography.Font_weight.Medium.$value,
                                fontSize: typography.Size.Text.xs.$value,
                                color: colors.GrayLightMode[700].$value,
                              }}
                            >
                              {row.nice.toUpperCase()}
                            </Typography>
                            <Box sx={{ textAlign: "right" }}>
                              <Typography
                                sx={{
                                  fontFamily: typography.Font_Family.$value,
                                  fontWeight: typography.Font_weight.Semibold.$value,
                                  fontSize: typography.Size.Text.sm.$value,
                                  color: colors.GrayLightMode[900].$value,
                                  textTransform: "capitalize",
                                }}
                              >
                                {cell.requirement}
                              </Typography>
                              <Typography sx={{ fontSize: 12, color: matchResultColor(cell.matchResult) }}>
                                {cell.matchResult}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
